/** QUOTE FIT CHECK — renders the quote at every spec density, shows the page
 *  count of each and applies the tightest fit in one click (saved on the quote). */
import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Scaling, Loader2, Check, RotateCcw } from "lucide-react";

export function QuoteFitCheck({ q, onFitChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const applied = res?.applied_mode ?? q.pdf_fit?.description_mode ?? null;
  const hint = q.pdf_fit_hint;
  const suggest = !!hint?.spill && !applied && !res;

  const run = async () => {
    setOpen(true); setBusy(true);
    try {
      const { data } = await api.get(`/vtiger/quotations/${q.id}/pdf/fit-check`);
      setRes(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Fit check failed"); setOpen(false); }
    setBusy(false);
  };

  const apply = async (mode) => {
    setBusy(true);
    try {
      await api.post(`/vtiger/quotations/${q.id}/pdf/fit`, { mode });
      toast.success(mode ? `${label(mode)} specifications applied — Preview, Download and the issued PDF now use it` : "Fit reset to the default density");
      setRes((r) => r ? { ...r, applied_mode: mode, options: r.options.map((o) => ({ ...o, applied: o.mode === mode })) } : r);
      onFitChanged?.(q.id, mode);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not apply"); }
    setBusy(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button onClick={run} data-testid="quote-fit-check" title={suggest ? hint.label : "Fit check — tighten specifications to fit fewer pages"}
          className={`inline-flex items-center gap-1 px-2.5 h-[30px] text-[12px] hover:bg-white ${applied ? "text-sky-700 font-medium" : suggest ? "text-amber-800 bg-amber-50 font-medium" : "text-slate-500 hover:text-slate-700"}`}>
          <Scaling className="h-3.5 w-3.5" /> Fit check{applied ? ` · ${label(applied)}` : ""}
          {suggest && <span data-testid="quote-fit-suggest" className="ml-1 rounded-full bg-amber-500 px-1.5 text-[10px] font-semibold text-white">saves {hint.saves} page{hint.saves !== 1 ? "s" : ""}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-3 z-[140]" data-testid="quote-fit-popover">
        {busy && !res ? (
          <div className="flex items-center gap-2 text-[12px] text-slate-600" data-testid="quote-fit-loading">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Rendering at every density…
          </div>
        ) : res ? (
          <div className="space-y-2">
            <div className="text-[12px] font-semibold text-slate-900" data-testid="quote-fit-message">{res.message}</div>
            <ul className="space-y-1">
              {res.options.map((o) => (
                <li key={o.mode} className="flex items-center justify-between gap-2 rounded border border-slate-200 px-2 py-1.5"
                  data-testid={`quote-fit-option-${o.mode.toLowerCase()}`}>
                  <div className="text-[12px]">
                    <span className="font-medium text-slate-800">{o.label}</span>
                    <span className="ml-1.5 text-slate-500" data-testid={`quote-fit-pages-${o.mode.toLowerCase()}`}>
                      {o.pages} page{o.pages !== 1 ? "s" : ""}
                    </span>
                    {o.current && <span className="ml-1.5 text-[10px] uppercase tracking-wide text-slate-400">current</span>}
                  </div>
                  {o.applied ? (
                    <span className="inline-flex items-center gap-1 text-[11px] text-sky-700"><Check className="h-3 w-3" /> Applied</span>
                  ) : (
                    <button disabled={busy} onClick={() => apply(o.mode)} data-testid={`quote-fit-apply-${o.mode.toLowerCase()}`}
                      className={`h-6 px-2 rounded text-[11px] font-medium ${res.recommendation?.mode === o.mode
                        ? "bg-slate-900 text-white hover:bg-slate-800" : "border border-slate-300 text-slate-700 hover:bg-slate-50"}`}>
                      {res.recommendation?.mode === o.mode ? `Apply → ${o.pages} page${o.pages !== 1 ? "s" : ""}` : "Use"}
                    </button>
                  )}
                </li>
              ))}
            </ul>
            {applied && (
              <button disabled={busy} onClick={() => apply(null)} data-testid="quote-fit-reset"
                className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-800">
                <RotateCcw className="h-3 w-3" /> Reset to default density
              </button>
            )}
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

const label = (m) => ({ FULL: "Detailed", STANDARD: "Standard", COMPACT: "Compact" }[m] || m);

export default QuoteFitCheck;
